// src/api/djangoApi.js
import axiosClient from "./axiosClient";

// Exporter axiosClient pour usage direct dans certains composants
export { axiosClient };

//
// 📚 CLASSES
//
export async function getClasses() {
  try {
    const { data } = await axiosClient.get("/classes/");
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des classes :", error);
    return [];
  }
}
export async function createClasse(payload) {
  try {
    const { data } = await axiosClient.post("/classes/", payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la création de la classe :", error);
    throw error;
  }
}
// Vue synthétique des classes (comptes élèves)
export async function getClassesOverview() {
  try {
    const { data } = await axiosClient.get("/classes/overview/");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("❌ Erreur overview classes :", error);
    return [];
  }
}
// Détails d'une classe: élèves + infos
export async function getClasseEleves(classeId) {
  try {
    const { data } = await axiosClient.get(`/classes/${classeId}/eleves/`);
    return data || { classe: null, count: 0, eleves: [] };
  } catch (error) {
    console.error("❌ Erreur chargement élèves de la classe :", error);
    return { classe: null, count: 0, eleves: [] };
  }
}
//
// 🧠 SYNTHESES DE PROFILS (cote prof) — module vendable
// Consomme les endpoints backend prof/classes ; re-throw pour laisser la page
// distinguer un 403 (module non active) d'une erreur reseau.
//
export async function getProfClasses() {
  const { data } = await axiosClient.get("/prof/classes/");
  return data?.classes ?? [];
}
export async function getProfClasseProfils(classeId) {
  const { data } = await axiosClient.get(`/prof/classes/${classeId}/profils/`);
  return data;
}

export async function updateClasse(id, payload) {
  try {
    const { data } = await axiosClient.put(`/classes/${id}/`, payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la mise à jour de la classe :", error);
    throw error;
  }
}
export async function deleteClasse(id) {
  try {
    await axiosClient.delete(`/classes/${id}/`);
    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la suppression de la classe :", error);
    throw error;
  }
}

//
// 📖 MATIÈRES-CLASSES (Subject management per class)
//
export async function getMatiereClasses(classeId) {
  try {
    const { data } = await axiosClient.get(`/matieres-classes/`, {
      params: { classe: classeId },
    });
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des matières de la classe :", error);
    return [];
  }
}

export async function createMatiereClasse(payload) {
  try {
    const { data } = await axiosClient.post("/matieres-classes/", payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la création de la matière-classe :", error);
    throw error;
  }
}

export async function updateMatiereClasse(id, payload) {
  try {
    const { data } = await axiosClient.patch(`/matieres-classes/${id}/`, payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la mise à jour de la matière-classe :", error);
    throw error;
  }
}

export async function deleteMatiereClasse(id) {
  try {
    await axiosClient.delete(`/matieres-classes/${id}/`);
    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la suppression de la matière-classe :", error);
    throw error;
  }
}

// Dédoublements : répartition des élèves d'une classe en demi-groupes.
export async function getGroupesClasse(classeId) {
  const { data } = await axiosClient.get(`/classes/${classeId}/groupes/`);
  return data;
}

export async function repartirClasseEnGroupes(classeId, nbGroupes = 2) {
  const { data } = await axiosClient.post(`/classes/${classeId}/groupes/`, { nb_groupes: nbGroupes });
  return data;
}

export async function reinitialiserGroupesClasse(classeId) {
  const { data } = await axiosClient.delete(`/classes/${classeId}/groupes/`);
  return data;
}

// Note: fonctions emplois déjà déclarées plus haut (dé-dupliquées)

//
// 👨‍🎓 UTILISATEURS
//
export async function getElevesByClasse(classeId) {
  try {
    const { data } = await axiosClient.get("/utilisateurs/");
    return data.filter((u) => u.role === "eleve" && u.classe === classeId);
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des élèves :", error);
    return [];
  }
}

// Liste complète des élèves (toutes classes)
export async function listEleves() {
  try {
    const { data } = await axiosClient.get("/utilisateurs/");
    return (Array.isArray(data) ? data : []).filter((u) => u.role === "eleve");
  } catch (error) {
    console.error("❌ Erreur listEleves :", error);
    return [];
  }
}
// Liste complète des professeurs
export async function listProfs() {
  try {
    const { data } = await axiosClient.get("/utilisateurs/");
    return (Array.isArray(data) ? data : []).filter((u) => u.role === "prof");
  } catch (error) {
    console.error("❌ Erreur listProfs :", error);
    return [];
  }
}

// Liste d'utilisateurs par rôle (prof, parent, directeur, administratif, eleve, ...)
export async function listUsersByRole(role) {
  try {
    const { data } = await axiosClient.get("/utilisateurs/");
    const rows = Array.isArray(data) ? data : [];
    return rows.filter((u) => String(u.role) === String(role));
  } catch (error) {
    console.error(`❌ Erreur listUsersByRole(${role}) :`, error);
    return [];
  }
}

// Liste de TOUS les utilisateurs
export async function listAllUtilisateurs() {
  try {
    const { data } = await axiosClient.get("/utilisateurs/");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("❌ Erreur listAllUtilisateurs :", error);
    return [];
  }
}

export async function createUtilisateur(payload) {
  try {
    const { data } = await axiosClient.post("/utilisateurs/", payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la création de l'utilisateur :", error);
    throw error;
  }
}

// Création d'un élève via l'endpoint admin (génère un username automatiquement)
export async function adminCreateEleve({ first_name, last_name, email, classe }) {
  try {
    const payload = { first_name, last_name, email, classe, role: "eleve" };
    const { data } = await axiosClient.post("/utilisateurs/admin/create-eleve/", payload);
    return data?.utilisateur || null;
  } catch (error) {
    console.error("❌ Erreur adminCreateEleve :", error);
    throw error;
  }
}
// Création d'un prof via l'endpoint admin (génère un username automatiquement)
export async function adminCreateProf({ first_name, last_name, email, matieres = [], classes = [] }) {
  try {
    const payload = { first_name, last_name, email, role: "prof" };
    if (Array.isArray(matieres) && matieres.length) payload.prof_matieres = matieres;
    if (Array.isArray(classes) && classes.length) payload.prof_classes = classes;
    const { data } = await axiosClient.post("/utilisateurs/admin/create-prof/", payload);
    return data?.utilisateur || null;
  } catch (error) {
    console.error("❌ Erreur adminCreateProf :", error);
    throw error;
  }
}
export async function updateUtilisateur(id, payload) {
  try {
    const { data } = await axiosClient.patch(`/utilisateurs/${id}/`, payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la mise à jour de l'utilisateur :", error);
    throw error;
  }
}
export async function deleteUtilisateur(id) {
  try {
    await axiosClient.delete(`/utilisateurs/${id}/`);
    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la suppression de l'utilisateur :", error);
    return false;
  }
}

//
// 📅 COURS — nouveau système (TimetableAssignment)
//
/**
 * Récupère les cours d'une classe pour une semaine donnée depuis le nouveau système EDT.
 * Retourne null si aucun EDT publié trouvé pour cette semaine (→ fallback ancien système).
 */
export async function getTimetableAssignmentsByClasse(classeId, weekStartYMD) {
  // Normalise un cours brut de l'API en objet attendu par les vues élève
  // (ajoute `heure` au format "HH:MM - HH:MM" et `prof` lisible).
  const normalize = (c) => ({
    ...c,
    heure: c.heure || (c.heure_debut && c.heure_fin
      ? `${String(c.heure_debut).slice(0, 5)} - ${String(c.heure_fin).slice(0, 5)}`
      : undefined),
    prof: c.prof || c.professeur_full_name || c.professeur_username || c.teacher_name || 'Professeur inconnu',
  });
  try {
    // Use the dedicated my-schedule endpoint (role-aware, returns Cours-like objects)
    const { data } = await axiosClient.get('/timetables/my-schedule/', {
      params: { week_start: weekStartYMD, mode: 'eleve' }
    });
    const assignments = Array.isArray(data) ? data : [];
    if (assignments.length === 0) return null;
    return assignments.map(normalize).filter((c) => c.heure);
  } catch (error) {
    // Fallback: try the old embedded-data approach
    try {
      const { data } = await axiosClient.get('/timetables/');
      const timetables = Array.isArray(data) ? data : (data.results || []);
      const tt = timetables.find(
        (t) => t.status === 'PUBLISHED' && t.week_start_date === weekStartYMD
      );
      if (!tt || !tt.active_variant_details?.assignments) return null;

      const [y, m, d] = weekStartYMD.split('-').map(Number);
      const weekStart = new Date(y, m - 1, d);

      return tt.active_variant_details.assignments
        .filter((a) => Number(a.class_group) === Number(classeId))
        .map((a) => {
          const day = new Date(weekStart);
          day.setDate(weekStart.getDate() + a.day);
          const yyyy = day.getFullYear();
          const mm = String(day.getMonth() + 1).padStart(2, '0');
          const dd = String(day.getDate()).padStart(2, '0');
          return {
            date: `${yyyy}-${mm}-${dd}`,
            heure: `${String(a.start_time).slice(0, 5)} - ${String(a.end_time).slice(0, 5)}`,
            matiere: a.subject_name || '',
            salle: a.room_name || 'Salle inconnue',
            prof: a.teacher_name || 'Professeur non assigné',
          };
        });
    } catch (fallbackError) {
      console.error('❌ Erreur timetable assignments (fallback):', fallbackError);
      return null;
    }
  }
}

//
// 📅 COURS — ancien système (legacy)
//
export async function getEmploiDuTempsByClasse(classeId) {
  // Try new timetable system first (published EDT for current week)
  try {
    const now = new Date();
    const day = now.getDay(); // 0=Sun
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((day + 6) % 7));
    const pad = (n) => String(n).padStart(2, '0');
    const weekStart = `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`;
    const { data } = await axiosClient.get('/timetables/my-schedule/', {
      params: { week_start: weekStart, mode: 'eleve' },
    });
    const rows = Array.isArray(data) ? data : [];
    if (rows.length > 0) {
      return rows.map((c) => ({
        ...c,
        heure: c.heure || `${String(c.heure_debut).slice(0, 5)} - ${String(c.heure_fin).slice(0, 5)}`,
        prof: c.prof || c.professeur_full_name || 'Professeur inconnu',
      }));
    }
  } catch (_) { /* fallback to legacy */ }
  // Legacy fallback
  try {
    const { data } = await axiosClient.get("/cours/");
    return data
      .filter((c) => c.classe === classeId)
      .map((c) => ({
        ...c,
        heure: `${String(c.heure_debut).slice(0, 5)} - ${String(c.heure_fin).slice(0, 5)}`,
        prof: c.professeur_full_name || c.professeur_username || "Professeur inconnu",
      }));
  } catch (error) {
    console.error("❌ Erreur lors de la récupération de l'emploi du temps :", error);
    return [];
  }
}

// 👨‍👧 COURS d'un enfant (vue parent) — nouveau système EDT + fallback legacy
export async function getEmploiDuTempsParent(eleveId, weekStartYMD) {
  // Essayer le nouveau système EDT d'abord
  if (weekStartYMD) {
    try {
      const { data } = await axiosClient.get("/timetables/my-schedule/", {
        params: { week_start: weekStartYMD, eleve_id: eleveId }
      });
      const rows = Array.isArray(data) ? data : [];
      if (rows.length > 0) return rows;
    } catch (_) { /* fallback to legacy */ }
  }
  // Legacy: /cours/parent/
  try {
    const { data } = await axiosClient.get("/cours/parent/", { params: { eleve_id: eleveId } });
    return (Array.isArray(data) ? data : []).map((c) => ({
      ...c,
      heure: `${String(c.heure_debut).slice(0, 5)} - ${String(c.heure_fin).slice(0, 5)}`,
      prof: c.professeur_full_name || c.professeur_username || "Professeur inconnu",
    }));
  } catch (error) {
    console.error("❌ Erreur lors de la récupération de l'EDT enfant :", error);
    return [];
  }
}

// 👨‍🏫 COURS du professeur connecté — nouveau système EDT + fallback legacy
export async function getCoursMineProf(params = {}) {
  // Si weekStart est fourni, essayer d'abord le nouveau système EDT
  if (params.weekStart) {
    try {
      const { data } = await axiosClient.get("/timetables/my-schedule/", {
        params: { week_start: params.weekStart }
      });
      const rows = Array.isArray(data) ? data : [];
      if (rows.length > 0) return rows;
    } catch (_) { /* fallback to legacy */ }
  }
  // Legacy: /cours/mine/
  try {
    const query = {};
    if (params.date) query.date = params.date;
    if (params.dateFrom) query.date_from = params.dateFrom;
    if (params.dateTo) query.date_to = params.dateTo;
    const { data } = await axiosClient.get("/cours/mine/", { params: query });
    let rows = Array.isArray(data) ? data : [];
    // Normalisation heure "HH:MM - HH:MM" + champs utiles d'affichage
    rows = rows.map((c) => ({
      ...c,
      heure: `${String(c.heure_debut).slice(0, 5)} - ${String(c.heure_fin).slice(0, 5)}`,
      prof: c.professeur_full_name || c.professeur_username || "Moi",
    }));
    return rows;
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des cours du prof :", error);
    return [];
  }
}

export async function getCoursProchainsMine(days = 7) {
  // Try new timetable system first (published EDT for current week)
  try {
    const now = new Date();
    const day = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((day + 6) % 7));
    const pad = (n) => String(n).padStart(2, '0');
    const weekStart = `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`;
    const { data } = await axiosClient.get('/timetables/my-schedule/', {
      params: { week_start: weekStart },
    });
    const rows = Array.isArray(data) ? data : [];
    if (rows.length > 0) {
      return rows.map((c) => ({
        ...c,
        heure: c.heure || `${String(c.heure_debut).slice(0, 5)} - ${String(c.heure_fin).slice(0, 5)}`,
        prof: c.prof || c.professeur_full_name || 'Moi',
      }));
    }
  } catch (_) { /* fallback to legacy */ }
  // Legacy fallback
  try {
    const { data } = await axiosClient.get("/cours/prochains/", { params: { days } });
    let rows = Array.isArray(data) ? data : [];
    rows = rows.map((c) => ({
      ...c,
      heure: `${String(c.heure_debut).slice(0, 5)} - ${String(c.heure_fin).slice(0, 5)}`,
      prof: c.professeur_full_name || c.professeur_username || "Moi",
    }));
    return rows;
  } catch (error) {
    console.error("❌ Erreur lors du chargement des prochains cours :", error);
    return [];
  }
}

// 🔒 Cours archivés (feuilles d'appel verrouillées) du prof connecté, visibles sur les X derniers jours (défaut: 5)
export async function getCoursArchivesMine(daysBack = 5) {
  try {
    const to = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const toStr = `${to.getFullYear()}-${pad(to.getMonth() + 1)}-${pad(to.getDate())}`;
    const from = new Date(to.getTime() - daysBack * 24 * 60 * 60 * 1000);
    const fromStr = `${from.getFullYear()}-${pad(from.getMonth() + 1)}-${pad(from.getDate())}`;
    const rows = await getCoursMineProf({ dateFrom: fromStr, dateTo: toStr });
    // Filtrer: archivé ET (archived_at ou date de cours) dans la fenêtre des X derniers jours
    const fromMs = from.setHours(0,0,0,0);
    const toMs = to.setHours(23,59,59,999);
    const parseDate = (s) => {
      if (!s) return null;
      // supporte ISO ou "YYYY-MM-DD HH:MM"; fallback Date()
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    };
    return rows.filter((c) => {
      if (!c.appel_archived) return false;
      const archivedAtStr = c.appel_archived_at || c.archived_at;
      const archivedAt = parseDate(archivedAtStr);
      const inRangeByArchived = archivedAt ? (archivedAt.getTime() >= fromMs && archivedAt.getTime() <= toMs) : false;
      if (inRangeByArchived) return true;
      // fallback par date du cours si archived_at manquant
      const d = parseDate(c.date);
      return d ? (d.getTime() >= fromMs && d.getTime() <= toMs) : false;
    });
  } catch (error) {
    console.error("❌ Erreur lors du chargement des cours archivés :", error);
    return [];
  }
}
export async function createCours(payload) {
  try {
    const { data } = await axiosClient.post("/cours/", payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la création du cours :", error);
    throw error;
  }
}
export async function updateCours(id, payload) {
  try {
    const { data } = await axiosClient.put(`/cours/${id}/`, payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la mise à jour du cours :", error);
    throw error;
  }
}
export async function deleteCours(id) {
  try {
    await axiosClient.delete(`/cours/${id}/`);
    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la suppression du cours :", error);
    return false;
  }
}

export async function getCoursById(id) {
  try {
    const { data } = await axiosClient.get(`/cours/${id}/`);
    if (data && data.heure_debut && data.heure_fin) {
      data.heure = `${String(data.heure_debut).slice(0,5)} - ${String(data.heure_fin).slice(0,5)}`;
    }
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la récupération du cours :", error);
    return null;
  }
}
// Tous les cours (utile pour agréger les classes/matières par prof côté admin)
export async function getAllCours() {
  try {
    const { data } = await axiosClient.get("/cours/");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("❌ Erreur getAllCours :", error);
    return [];
  }
}

// 📅 Calendrier – WebCal
export async function generateCalendarToken() {
  const { data } = await axiosClient.post("/auth/calendar/token/generate/");
  return data?.calendar_token || "";
}
export async function revokeCalendarToken() {
  await axiosClient.post("/auth/calendar/token/revoke/");
  return true;
}
export function buildWebcalUrl(token) {
  if (!token) return "";
  // Utiliser l'URL courante comme base (traite proxy CRA en dev)
  const loc = window.location;
  // Chemin API (CRA proxy redirige /api → backend)
  const httpUrl = `${loc.protocol}//${loc.host}/api/cours/ics/sub/${token}/?days=180`;
  // Les clients supportent souvent webcal:// (équivalent à http(s)://)
  return httpUrl.replace(/^https?:\/\//i, "webcal://");
}

//
// 📘 DEVOIRS
//

// 🔎 Devoirs de l’élève connecté
// Backend: classe de l'élève sur fenêtre [today-14j, today+60j].
// Options possibles: { past_days, future_days, status: 'todo'|'done'|'late', matiere, type }
export async function getDevoirsMine(params = {}) {
  try {
    const { data } = await axiosClient.get("/devoirs/mine/", { params });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des devoirs (mine) :", error);
    return [];
  }
}

// 📊 Statistiques élève (KPI)
export async function getDevoirsStats() {
  try {
    const { data } = await axiosClient.get("/devoirs/stats/");
    return data || { todo: 0, urgent: 0, total_estime_minutes: 0, faits_cette_semaine: 0, en_retard: 0, par_matiere: {} };
  } catch (error) {
    console.error("❌ Erreur lors du chargement des stats devoirs :", error);
    return { todo: 0, urgent: 0, total_estime_minutes: 0, faits_cette_semaine: 0, en_retard: 0, par_matiere: {} };
  }
}

// ⏱️ Charge de travail quotidienne (élève)
//   params: { offset?: number }  // 0 = aujourd'hui, +1 = demain, etc.
export async function getDevoirsWorkloadDaily(params = {}) {
  try {
    const { data } = await axiosClient.get("/devoirs/workload/daily/", { params });
    return data || { date: null, total_minutes: 0, status: "aucun", count: 0, items: [] };
  } catch (error) {
    console.error("❌ Erreur lors du chargement de la charge quotidienne :", error);
    return { date: null, total_minutes: 0, status: "aucun", count: 0, items: [] };
  }
}

// ⏱️ Charge de travail hebdomadaire (élève)
//   params: { week_offset?: number }
export async function getDevoirsWorkloadWeekly(params = {}) {
  try {
    const { data } = await axiosClient.get("/devoirs/workload/weekly/", { params });
    return data || { monday: null, sunday: null, total_minutes: 0, days: [] };
  } catch (error) {
    console.error("❌ Erreur lors du chargement de la charge hebdomadaire :", error);
    return { monday: null, sunday: null, total_minutes: 0, days: [] };
  }
}

// 🔁 Recalcule la durée système d'un devoir (prof/owner uniquement)
//    overrides optionnels: { homework_type, difficulty, volume_type, volume_quantity,
//                           is_graded, is_required, started_in_class }
export async function recalculateDevoirDuration(devoirId, overrides = {}) {
  try {
    const { data } = await axiosClient.post(
      `/devoirs/${devoirId}/recalculate-duration/`,
      overrides
    );
    return data;
  } catch (error) {
    console.error("❌ Erreur lors du recalcul de durée :", error);
    return null;
  }
}

// 🔎 (optionnel) même résultat via alias backend /prochains/
export async function getDevoirsProchains() {
  try {
    const { data } = await axiosClient.get("/devoirs/prochains/");
    return data;
  } catch (error) {
    console.error("❌ Erreur lors du chargement des devoirs (prochains) :", error);
    return [];
  }
}

// 📦 Tous les devoirs (utile pour reconstituer “passés” côté front)
export async function getAllDevoirs() {
  try {
    const { data } = await axiosClient.get("/devoirs/");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des devoirs :", error);
    return [];
  }
}

// ✅ Marquer / dé-marquer un devoir comme fait (conforme à DRF: set_fait)
export async function setDevoirFait(devoirId, fait = true) {
  try {
    const { data } = await axiosClient.post(`/devoirs/${devoirId}/set_fait/`, { fait });
    return data;
  } catch (error) {
    console.error("❌ Erreur lors du changement d’état du devoir :", error);
    throw error;
  }
}

// 🔁 (facultatif) bascule fait/pas fait côté serveur
export async function toggleDevoirFait(devoirId) {
  try {
    const { data } = await axiosClient.post(`/devoirs/${devoirId}/toggle_fait/`);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors du toggle d’état du devoir :", error);
    throw error;
  }
}

// (facultatif) CRUD standard pour profs/admins
export async function createDevoir(payload) {
  try {
    const { data } = await axiosClient.post("/devoirs/", payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la création du devoir :", error);
    throw error;
  }
}
export async function updateDevoir(id, payload) {
  try {
    const { data } = await axiosClient.put(`/devoirs/${id}/`, payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la mise à jour du devoir :", error);
    throw error;
  }
}
export async function deleteDevoir(id) {
  try {
    await axiosClient.delete(`/devoirs/${id}/`);
    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la suppression du devoir :", error);
    return false;
  }
}

//
// 📝 NOTES
//
/**
 * Notes de l’élève connecté.
 * Attendu par BlocDerniereNote (champs utilisés : matiere, note, coeff?, commentaire?, date).
 */
export async function getNotesMine() {
  try {
    const { data } = await axiosClient.get("/notes/mine/");
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des notes (mine) :", error);
    return [];
  }
}

/**
 * Synthèse des notes de l'élève (V2 design "Mes notes").
 * Renvoie moyenne générale, position classe, médiane classe, moyennes par matière, etc.
 * @param {{periode?:string, annee?:string}} params
 */
export async function getNotesSynthese({ periode, annee } = {}) {
  try {
    const qs = [];
    if (periode) qs.push(`periode=${encodeURIComponent(periode)}`);
    if (annee) qs.push(`annee=${encodeURIComponent(annee)}`);
    const url = `/utilisateurs/me/notes-synthese/${qs.length ? `?${qs.join("&")}` : ""}`;
    const { data } = await axiosClient.get(url);
    return data || null;
  } catch (error) {
    console.error("❌ Erreur lors du chargement de la synthèse notes :", error);
    return null;
  }
}

/**
 * Statistiques agrégées pour le dashboard de l'élève connecté.
 * Renvoie absences/retards/assiduité 30j, moyennes élève/classe, évolution mensuelle (8 mois)
 * et moyennes par matière de la classe. Renvoie null en cas d'erreur (le caller affiche un fallback).
 */
export async function getDashboardStats() {
  try {
    const { data } = await axiosClient.get("/utilisateurs/me/dashboard-stats/");
    return data || null;
  } catch (error) {
    console.error("❌ Erreur lors du chargement des stats dashboard :", error);
    return null;
  }
}

/**
 * Signaux Senso IA (heuristiques côté backend) pour l'élève connecté.
 * Format attendu: { signals: Array<{kind, title, text, matiere?}>, generated_at, approximate }
 */
export async function getSensoSignals() {
  try {
    const { data } = await axiosClient.get("/utilisateurs/me/senso-signals/");
    return data || null;
  } catch (error) {
    console.error("❌ Erreur lors du chargement des signaux Senso :", error);
    return null;
  }
}

/**
 * Badges (catalog calculé) de l'élève connecté.
 * Renvoie un tableau de badges {code, label, description, icon, earned, progress, earned_at}.
 */
export async function getMyBadges() {
  try {
    const { data } = await axiosClient.get("/utilisateurs/me/badges/");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des badges :", error);
    return [];
  }
}

/**
 * Série journalière 30 jours pour les sparklines KPI.
 * Renvoie un tableau de 30 entrées {date, absences, retards, assiduite, devoirs_rendus, moyenne}.
 */
export async function getKpiHistory() {
  try {
    const { data } = await axiosClient.get("/utilisateurs/me/kpi-history/");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement de l'historique KPI :", error);
    return [];
  }
}

// (facultatif) toutes les notes visibles (selon droits) — utile pour pages admin/prof
export async function getNotes(params = {}) {
  try {
    const { data } = await axiosClient.get("/notes/", { params });
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des notes :", error);
    return [];
  }
}

// (facultatif) récupérer les notes d’un élève spécifique (pour prof/admin)
export async function getNotesByEleve(eleveId) {
  try {
    const { data } = await axiosClient.get(`/notes/?eleve=${eleveId}`);
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des notes de l'élève :", error);
    return [];
  }
}

// CRUD (pour profs/admins)
export async function createNote(payload) {
  try {
    const { data } = await axiosClient.post("/notes/", payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la création de la note :", error);
    throw error;
  }
}
export async function updateNote(id, payload) {
  try {
    const { data } = await axiosClient.put(`/notes/${id}/`, payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la mise à jour de la note :", error);
    throw error;
  }
}
export async function deleteNote(id) {
  try {
    await axiosClient.delete(`/notes/${id}/`);
    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la suppression de la note :", error);
    return false;
  }
}

// Publication explicite d'une note (passe is_published=true).
export async function publishNote(id) {
  try {
    const { data } = await axiosClient.post(`/notes/${id}/publier/`);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la publication de la note :", error);
    throw error;
  }
}

// Publication en lot.
export async function bulkPublishNotes(ids = []) {
  try {
    const { data } = await axiosClient.post(`/notes/bulk_publish/`, { ids });
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la publication en lot :", error);
    throw error;
  }
}

// ---------------- ADMIN / DOCUMENTS REQUIS ----------------
export async function listDocumentRequisTypes(){
  const { data } = await axiosClient.get('/documents/requis/types/');
  return data;
}
export async function createDocumentRequisType(payload){
  const { data } = await axiosClient.post('/documents/requis/types/', payload); return data;
}
export async function updateDocumentRequisType(id,payload){
  const { data } = await axiosClient.put(`/documents/requis/types/${id}/`, payload); return data;
}
export async function listDocumentsRequisEleves(params={}){
  const { data } = await axiosClient.get('/documents/requis/eleves/', { params }); return data;
}
export async function uploadDocumentRequisEleve(payload){
  const form = new FormData();
  Object.entries(payload).forEach(([k,v])=> { if(v!==undefined && v!==null) form.append(k,v); });
  const { data } = await axiosClient.post('/documents/requis/eleves/', form, { headers:{'Content-Type':'multipart/form-data'} });
  return data;
}
export async function validerDocumentRequis(id){
  const { data } = await axiosClient.post(`/documents/requis/eleves/${id}/valider/`); return data;
}
export async function refuserDocumentRequis(id, commentaire_refus){
  const { data } = await axiosClient.post(`/documents/requis/eleves/${id}/refuser/`, { commentaire_refus }); return data;
}
export async function genererDocsManquants(classe){
  const { data } = await axiosClient.post('/documents/requis/eleves/generer-manquants/', { classe }); return data;
}

// ---------------- ADMIN / STATS ----------------
export async function getAdminStatsDocuments(){
  const { data } = await axiosClient.get('/admin/stats/documents/'); return data;
}
export async function getAdminStatsJustifs(){
  const { data } = await axiosClient.get('/admin/stats/justifs/'); return data;
}

// ---------------- PAIEMENTS SCOLAIRES (admin + parent) ----------------
export async function listPaiementsScolaires(params = {}) {
  const { data } = await axiosClient.get('/paiements-scolaires/', { params });
  return Array.isArray(data) ? data : data.results || [];
}
export async function getPaiementsScolairesStats() {
  const { data } = await axiosClient.get('/paiements-scolaires/stats/');
  return data;
}
export async function createPaiementScolaire(payload) {
  const { data } = await axiosClient.post('/paiements-scolaires/', payload);
  return data;
}
export async function updatePaiementScolaire(id, payload) {
  const { data } = await axiosClient.patch(`/paiements-scolaires/${id}/`, payload);
  return data;
}
export async function deletePaiementScolaire(id) {
  await axiosClient.delete(`/paiements-scolaires/${id}/`);
}
export async function marquerPaiementPaye(id) {
  const { data } = await axiosClient.post(`/paiements-scolaires/${id}/marquer-paye/`);
  return data;
}
export async function createStripeIntent(paiementId) {
  const { data } = await axiosClient.post(`/paiements-scolaires/${paiementId}/stripe-intent/`);
  return data; // { client_secret, amount }
}
export async function createPaypalOrder(paiementId) {
  const { data } = await axiosClient.post(`/paiements-scolaires/${paiementId}/paypal-order/`);
  return data; // { order_id }
}
export async function confirmOnlinePayment(paiementId, provider, paymentId) {
  const { data } = await axiosClient.post(`/paiements-scolaires/${paiementId}/confirm-payment/`, { provider, payment_id: paymentId });
  return data;
}

// ---- Échéancier (mensualisation) ----
export async function mensualiserPaiement(paiementId, nombreEcheances = 3) {
  const { data } = await axiosClient.post(`/paiements-scolaires/${paiementId}/mensualiser/`, { nombre_echeances: nombreEcheances });
  return data;
}
export async function listEcheances(paiementId) {
  const { data } = await axiosClient.get('/echeances/', { params: { paiement: paiementId } });
  return Array.isArray(data) ? data : data.results || [];
}
export async function payerEcheance(echeanceId) {
  const { data } = await axiosClient.post(`/echeances/${echeanceId}/payer/`);
  return data;
}

// ---- Bourses et aides ----
export async function listBoursesAides(eleveId) {
  const { data } = await axiosClient.get('/bourses-aides/', { params: { eleve: eleveId } });
  return Array.isArray(data) ? data : data.results || [];
}
export async function createBourseAide(payload) {
  const { data } = await axiosClient.post('/bourses-aides/', payload);
  return data;
}
export async function validerBourseAide(id) {
  const { data } = await axiosClient.post(`/bourses-aides/${id}/valider/`);
  return data;
}
export async function refuserBourseAide(id) {
  const { data } = await axiosClient.post(`/bourses-aides/${id}/refuser/`);
  return data;
}

// ---- Partage multi-responsables ----
export async function partagerPaiement(paiementId, repartition = null) {
  const payload = repartition ? { repartition } : {};
  const { data } = await axiosClient.post(`/paiements-scolaires/${paiementId}/partager/`, payload);
  return data;
}
export async function getParentsEleve(paiementId) {
  const { data } = await axiosClient.get(`/paiements-scolaires/${paiementId}/parents-eleve/`);
  return data;
}

// ---- Relances ----
export async function listRelances() {
  const { data } = await axiosClient.get('/relances-paiements/');
  return Array.isArray(data) ? data : data.results || [];
}

// Élèves de l'établissement (pour le sélecteur admin)
export async function listElevesEtablissement() {
  const { data } = await axiosClient.get('/utilisateurs/', { params: { role: 'eleve' } });
  return Array.isArray(data) ? data : data.results || [];
}


// -------- Marketing interest (landing votes) --------
export async function postLandingInterest(vote, meta={}){
  const payload = { vote: !!vote, path: window.location.pathname, ...meta };
  const { data } = await axiosClient.post('/marketing/interest/vote/', payload);
  return data;
}
export async function getLandingInterestStats(){
  const { data } = await axiosClient.get('/marketing/interest/stats/');
  return data;
}

// ---------------- ADMIN / EMPLOI DU TEMPS GLOBAL ----------------
export async function adminSearchCours(params={}){
  const { data } = await axiosClient.get('/cours/admin/search/', { params });
  const rows = (data?.results||[]).map(c=> ({
    ...c,
    heure: `${String(c.heure_debut).slice(0,5)} - ${String(c.heure_fin).slice(0,5)}`
  }));
  return { rows, count: data?.count || rows.length };
}

// ---------------- ADMIN / BULK IMPORT UTILISATEURS ----------------
export async function bulkImportUsers(file, { dryRun=true } = {}){
  const form = new FormData();
  form.append('file', file);
  form.append('dry_run', dryRun ? 'true':'false');
  const { data } = await axiosClient.post('/utilisateurs/admin/bulk-import/', form, { headers:{'Content-Type':'multipart/form-data'} });
  return data;
}

// ---------------- ADMIN / BROADCAST MESSAGES ----------------
export async function broadcastClasse(classe, { sujet='', contenu }){
  const { data } = await axiosClient.post('/messages/admin/broadcast-classe/', { classe, sujet, contenu });
  return data;
}
export async function broadcastRoles(roles, { sujet='', contenu }){
  const { data } = await axiosClient.post('/messages/admin/broadcast-roles/', { roles, sujet, contenu });
  return data;
}

// ---------------- MESSAGES CIBLÉS (prof / directeur) ----------------
export async function broadcastClasseCible(classe, { sujet='', contenu }){
  const { data } = await axiosClient.post('/messages/broadcast-classe/', { classe, sujet, contenu });
  return data;
}
export async function broadcastGroupe(destinataires, { sujet='', contenu }){
  const { data } = await axiosClient.post('/messages/broadcast-groupe/', { destinataires, sujet, contenu });
  return data;
}

// ---------------- ADMIN / JUSTIFICATIONS D'ABSENCE ----------------
export async function listJustificationsAbsences(params={}){
  const { data } = await axiosClient.get('/justifications/absences/', { params });
  return data;
}
export async function validerJustification(id){
  const { data } = await axiosClient.post(`/absences/justifications/${id}/valider/`);
  return data;
}
export async function refuserJustification(id, commentaire_refus=''){
  const { data } = await axiosClient.post(`/absences/justifications/${id}/refuser/`, { commentaire_refus });
  return data;
}

//
// 🗒️ PRESENCES (feuille d'appel)
//
export async function getPresencesForCourse(coursId) {
  try {
    const { data } = await axiosClient.get(`/presences/for_course`, { params: { cours: coursId } });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des présences :", error);
    return [];
  }
}

export async function bulkSetPresences(coursId, states) {
  try {
  const { data } = await axiosClient.post(`/presences/bulk_set/`, { cours: coursId, states });
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de l'enregistrement des présences :", error);
    // Renvoyer l'erreur pour gestion UI (ex: 403 hors créneau)
    throw error;
  }
}

export async function getPresenceSummary(coursId) {
  try {
    const { data } = await axiosClient.get(`/presences/summary`, { params: { cours: coursId } });
    return data || { present: 0, absent: 0, total: 0 };
  } catch (error) {
    console.error("❌ Erreur lors du chargement du résumé des présences :", error);
    return { present: 0, absent: 0, total: 0 };
  }
}

export async function getPresenceWindow(coursId) {
  try {
    const { data } = await axiosClient.get(`/presences/window`, { params: { cours: coursId } });
    return data;
  } catch (error) {
    console.error("❌ Erreur lors du chargement de la fenêtre d'édition :", error);
    return null;
  }
}

export async function bulkSetPresencesForce(coursId, states) {
  try {
    const { data } = await axiosClient.post(`/presences/bulk_set/?force=1`, { cours: coursId, states, force: 1 });
    return data;
  } catch (error) {
    console.error("❌ Erreur (force) lors de l'enregistrement des présences :", error);
    throw error;
  }
}

export async function getPresenceSummaryForEleve(eleveId, params = {}) {
  try {
    const query = { eleve: eleveId };
    if (params.dateFrom) query.date_from = params.dateFrom;
    if (params.dateTo) query.date_to = params.dateTo;
    // Récupère toutes les présences de l'élève et calcule un résumé
    const { data } = await axiosClient.get(`/presences/`, { params: query });
    const rows = Array.isArray(data) ? data : [];
    const total = rows.length;
    const present = rows.filter((r) => r.present).length;
    const absent = total - present;
    return { total, present, absent };
  } catch (error) {
    console.error("❌ Erreur lors du chargement des présences (élève) :", error);
    return { total: 0, present: 0, absent: 0 };
  }
}

// Absences parent (30 derniers jours) enrichies (justifications incluses)
export async function getParentAbsences() {
  try {
    const { data } = await axiosClient.get('/utilisateurs/parent/absences/');
    return data?.enfants || [];
  } catch (e) {
    console.error('❌ Erreur getParentAbsences :', e);
    return [];
  }
}

// Devoirs d'un enfant (vue parent) — renvoie la liste enrichie (fait, en_retard, retard_minutes).
// Endpoint dédié parent : /utilisateurs/parent/devoirs/?eleve=<id>
export async function getParentDevoirsEnfant(eleveId) {
  if (!eleveId) return [];
  try {
    const { data } = await axiosClient.get('/utilisateurs/parent/devoirs/', { params: { eleve: eleveId } });
    return Array.isArray(data?.devoirs) ? data.devoirs : [];
  } catch (e) {
    console.error('❌ Erreur getParentDevoirsEnfant :', e);
    return [];
  }
}

// soumission d'un rendu de devoir (fichier)
export async function submitRenduDevoir(devoirId, file){
  try{
    const form=new FormData();
    form.append('fichier', file);
    const {data}=await axiosClient.post(`/devoirs/${devoirId}/submit_rendu/`, form, {headers:{'Content-Type':'multipart/form-data'}});
    return data;
  }catch(e){
    console.error('❌ Erreur dépôt rendu devoir :', e);
    throw e;
  }
}

// récupère les rendus d'un devoir (liste des fichiers + états)
export async function getRendusDevoir(devoirId){
  try{
    const url = typeof devoirId === 'string' && devoirId.includes('?') ? `/devoirs/${devoirId}/rendus/`.replace(/\/rendus\/rendus\//,'/rendus/') : `/devoirs/${devoirId}/rendus/`;
    const {data}=await axiosClient.get(url);
    return data;
  }catch(e){
    console.error('❌ Erreur récupération rendus devoir :', e);
    return {ok:false, etats:[]};
  }
}

// ---- QCM ----
export async function getQCMQuestions(devoirId){
  try {
    const {data} = await axiosClient.get(`/devoirs/${devoirId}/qcm_questions/`);
    return Array.isArray(data) ? data : (data.created || data || []);
  } catch(e){
    console.error('❌ Erreur chargement questions QCM :', e);
    return [];
  }
}

export async function createQCMQuestions(devoirId, questions){
  try {
    const payload = Array.isArray(questions) ? questions : [questions];
    const {data} = await axiosClient.post(`/devoirs/${devoirId}/qcm_questions/`, payload);
    return data;
  } catch(e){
    console.error('❌ Erreur création questions QCM :', e);
    throw e;
  }
}

export async function updateQCMQuestion(devoirId, partial){
  try {
    const {data} = await axiosClient.patch(`/devoirs/${devoirId}/qcm_question/`, partial);
    return data;
  } catch(e){
    console.error('❌ Erreur mise à jour question QCM :', e);
    throw e;
  }
}

export async function deleteQCMQuestion(devoirId, id){
  try {
    await axiosClient.delete(`/devoirs/${devoirId}/qcm_question/`, { data: { id }});
    return true;
  } catch(e){
    console.error('❌ Erreur suppression question QCM :', e);
    return false;
  }
}

export async function submitQCM(devoirId, responses){
  try {
    const {data} = await axiosClient.post(`/devoirs/${devoirId}/qcm_submit/`, { responses });
    return data;
  } catch(e){
    console.error('❌ Erreur soumission QCM :', e);
    throw e;
  }
}

export async function getQCMResultats(devoirId){
  try {
    const {data} = await axiosClient.get(`/devoirs/${devoirId}/qcm_resultats/`);
    return data;
  } catch(e){
    console.error('❌ Erreur récupération résultats QCM :', e);
    return null;
  }
}

// ----- Justifications d'absence -----
export async function listJustificationsAbsence(params = {}) {
  try {
    const { data } = await axiosClient.get('/absences/justifications/', { params });
    return Array.isArray(data) ? data : (data?.results || []);
  } catch (e) {
    console.error('❌ Erreur listJustificationsAbsence :', e);
    return [];
  }
}

export async function createJustificationAbsence({ eleve, date_debut, date_fin, motif = '', cours = null, file = null }) {
  const form = new FormData();
  form.append('eleve', eleve);
  form.append('date_debut', date_debut);
  form.append('date_fin', date_fin || date_debut);
  if (motif) form.append('motif', motif);
  if (cours) form.append('cours', cours);
  if (file) form.append('piece_jointe', file);
  try {
    const { data } = await axiosClient.post('/absences/justifications/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  } catch (e) {
    console.error('❌ Erreur création justification absence :', e);
    throw e;
  }
}

export async function updateJustificationStatut(id, statut) {
  try {
    // Utiliser les endpoints spécifiques valider/refuser
    const endpoint = statut === 'acceptee' ? 'valider' : 'refuser';
    const { data } = await axiosClient.post(`/absences/justifications/${id}/${endpoint}/`);
    return data;
  } catch (e) {
    console.error('❌ Erreur mise à jour statut justification :', e);
    throw e;
  }
}

// ================= SUPERADMIN API =================

// Health check plateforme (DB, Stripe, email)
export async function getSuperadminHealth(){
  const { data } = await axiosClient.get('/superadmin/health/');
  return data;
}

// Etablissements – statut & lecture seule
export async function superadminSetEtablissementStatut(etabId, statut){
  try{
    const { data } = await axiosClient.post(`/etablissements/${etabId}/set-statut/`, { statut });
    return data;
  }catch(e){
    console.error('❌ Erreur set statut établissement :', e);
    throw e;
  }
}

export async function superadminSetEtablissementLectureSeule(etabId, enabled){
  try{
    const { data } = await axiosClient.post(`/etablissements/${etabId}/lecture-seule/`, { enabled });
    return data;
  }catch(e){
    console.error('❌ Erreur bascule lecture seule établissement :', e);
    throw e;
  }
}

export async function superadminUpdateAnnuaireData(etabId, annuaireData){
  try{
    const { data } = await axiosClient.patch(`/etablissements/${etabId}/update-annuaire-data/`, annuaireData);
    return data;
  }catch(e){
    console.error('❌ Erreur mise à jour données Annuaire :', e);
    throw e;
  }
}

// Global settings CRUD
export async function superadminListGlobalSettings(params={}){
  const { data } = await axiosClient.get('/superadmin/settings/', { params });
  return Array.isArray(data) ? data : (data?.results || []);
}
export async function superadminCreateGlobalSetting({ key, value }){
  const { data } = await axiosClient.post('/superadmin/settings/', { key, value });
  return data;
}
export async function superadminUpdateGlobalSetting(id, partial){
  const { data } = await axiosClient.patch(`/superadmin/settings/${id}/`, partial);
  return data;
}
export async function superadminDeleteGlobalSetting(id){
  await axiosClient.delete(`/superadmin/settings/${id}/`);
  return true;
}

// Holiday calendars CRUD
export async function superadminListHolidayCalendars(params={}){
  const { data } = await axiosClient.get('/superadmin/holidays/', { params });
  return Array.isArray(data) ? data : (data?.results || []);
}
export async function superadminCreateHolidayCalendar(payload){
  const { data } = await axiosClient.post('/superadmin/holidays/', payload);
  return data;
}
export async function superadminUpdateHolidayCalendar(id, partial){
  const { data } = await axiosClient.patch(`/superadmin/holidays/${id}/`, partial);
  return data;
}
export async function superadminDeleteHolidayCalendar(id){
  await axiosClient.delete(`/superadmin/holidays/${id}/`);
  return true;
}

// Impersonation begin/end
export async function superadminImpersonateBegin({ user, read_only=true, elevated=false, reason='' }){
  const { data } = await axiosClient.post('/superadmin/impersonate/begin/', { user, read_only, elevated, reason });
  return data;
}
export async function superadminImpersonateEnd(id){
  const { data } = await axiosClient.post('/superadmin/impersonate/end/', { id });
  return data;
}

// SuperAdmin — Démos réservées (DemoRequest)
export async function listSuperAdminDemos(params={}){
  const { data } = await axiosClient.get('/superadmin/demos/', { params });
  return Array.isArray(data) ? data : (data?.results || []);
}
export async function getSuperAdminDemosStats(){
  const { data } = await axiosClient.get('/superadmin/demos/stats/');
  return data;
}
export async function createSuperAdminDemo(payload){
  const { data } = await axiosClient.post('/superadmin/demos/', payload);
  return data;
}
export async function updateSuperAdminDemo(id, partial){
  const { data } = await axiosClient.patch(`/superadmin/demos/${id}/`, partial);
  return data;
}
export async function rescheduleSuperAdminDemo(id, payload){
  const { data } = await axiosClient.post(`/superadmin/demos/${id}/reschedule/`, payload);
  return data;
}

// Gestion configuration d'essai (les endpoints admins départementaux sont définis plus bas)

export async function superadminConfigurerEssai(etablissementId, config) {
  const { data } = await axiosClient.post(`/etablissements/${etablissementId}/configurer-essai/`, {
    duree_jours: config.duree_essai,
    nb_directeurs: config.nb_directeurs,
    nb_profs: config.nb_professeurs,
    nb_eleves: config.nb_eleves,
    nb_administratifs: config.nb_administratifs
  });
  return data;
}

export async function importUtilisateurs(file, etablissementId) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('etablissement_id', etablissementId);
  
  const { data } = await axiosClient.post('/import-utilisateurs/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return data;
}

export async function getEtablissementStats() {
  const { data } = await axiosClient.get('/etablissements/stats/');
  return data;
}

// ── Annuaire (data.gouv) ──
export async function getAnnuaireStats() {
  const { data } = await axiosClient.get('/etablissements/annuaire-stats/');
  return data;
}

export async function importAnnuaireFile(file, { dryRun = false, versionLabel = '' } = {}) {
  const form = new FormData();
  form.append('file', file);
  form.append('dry_run', dryRun);
  if (versionLabel) form.append('version_label', versionLabel);
  const { data } = await axiosClient.post('/etablissements/import-annuaire/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000, // 5 min pour gros fichiers
  });
  return data;
}

export async function browseAnnuaire(params = {}) {
  const { data } = await axiosClient.get('/etablissements/annuaire-browse/', { params });
  return data;
}

export async function getAnnuaireFilters() {
  const { data } = await axiosClient.get('/etablissements/annuaire-filters/');
  return data;
}

//
// 🎨 THÈME ÉTABLISSEMENT
//
export async function getEtablissementTheme(etabId) {
  const { data } = await axiosClient.get(`/etablissements/${etabId}/theme/`);
  return data;
}
export async function updateEtablissementTheme(etabId, colors) {
  const { data } = await axiosClient.post(`/etablissements/${etabId}/theme/update/`, colors);
  return data;
}
export async function uploadEtablissementLogo(etabId, file) {
  const form = new FormData();
  form.append('logo', file);
  const { data } = await axiosClient.post(`/etablissements/${etabId}/theme/logo/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
export async function resetEtablissementTheme(etabId) {
  const { data } = await axiosClient.post(`/etablissements/${etabId}/theme/reset/`);
  return data;
}

export async function updateEtablissementThemeScope(etabId, scope, targetRoles) {
  const { data } = await axiosClient.post(`/etablissements/${etabId}/theme/scope/`, {
    theme_scope: scope,
    theme_target_roles: targetRoles,
  });
  return data;
}

//
// 👤 ADMINS DÉPARTEMENTAUX — SUPPRIMÉS (2026-04)
// La gouvernance par admin départemental a été retirée. Les helpers historiques
// (listAdminsDepartementaux, createAdminDepartemental, updateAdminDepartemental,
// deleteAdminDepartemental, getAdminDepartemental) ne sont plus exportés.
// Utiliser /api/utilisateurs/ avec role="admin" + filtre par etablissement.
//

//
// 📄 DOCUMENTS ÉCOLE (distribution de documents aux parents)
//
export async function listDocumentsEcole(filters = {}) {
  try {
    const params = new URLSearchParams();
    if (filters.type_destinataire) params.append('type_destinataire', filters.type_destinataire);
    const url = `/documents/ecole/${params.toString() ? '?' + params.toString() : ''}`;
    const { data } = await axiosClient.get(url);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des documents école :", error);
    return [];
  }
}

export async function createDocumentEcole(formData) {
  try {
    const { data } = await axiosClient.post('/documents/ecole/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la création du document école :", error);
    throw error;
  }
}

export async function deleteDocumentEcole(id) {
  try {
    await axiosClient.delete(`/documents/ecole/${id}/`);
    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la suppression du document école :", error);
    throw error;
  }
}

//
// 📋 DEMANDES DOCUMENTS PARENTS (demandes spontanées avec deadline)
//
export async function listDemandesDocuments(filters = {}) {
  try {
    const params = new URLSearchParams();
    if (filters.statut) params.append('statut', filters.statut);
    if (filters.parent) params.append('parent', filters.parent);
    const url = `/documents/demandes-parents/${params.toString() ? '?' + params.toString() : ''}`;
    const { data } = await axiosClient.get(url);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des demandes documents :", error);
    return [];
  }
}

export async function createDemandeDocument(payload) {
  try {
    const { data } = await axiosClient.post('/documents/demandes-parents/', payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la création de la demande document :", error);
    throw error;
  }
}

export async function uploadDemandeDocument(id, formData) {
  try {
    const { data } = await axiosClient.post(`/documents/demandes-parents/${id}/upload/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de l'upload du document demandé :", error);
    throw error;
  }
}

export async function validerDemandeDocument(id, payload = {}) {
  try {
    const { data } = await axiosClient.post(`/documents/demandes-parents/${id}/valider/`, payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors de la validation du document :", error);
    throw error;
  }
}

export async function refuserDemandeDocument(id, payload) {
  try {
    const { data } = await axiosClient.post(`/documents/demandes-parents/${id}/refuser/`, payload);
    return data;
  } catch (error) {
    console.error("❌ Erreur lors du refus du document :", error);
    throw error;
  }
}

export async function deleteDemandeDocument(id) {
  try {
    await axiosClient.delete(`/documents/demandes-parents/${id}/`);
    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la suppression de la demande :", error);
    throw error;
  }
}

// -------------------------------------------------------------
// 🏫 Salles (CRUD)
// -------------------------------------------------------------
export async function listSalles() {
  try {
    const { data } = await axiosClient.get('/salles/');
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('❌ listSalles error', e);
    return [];
  }
}
export async function createSalle(payload) {
  const { data } = await axiosClient.post('/salles/', payload);
  return data;
}
export async function updateSalle(id, payload) {
  const { data } = await axiosClient.put(`/salles/${id}/`, payload);
  return data;
}
export async function deleteSalle(id) {
  await axiosClient.delete(`/salles/${id}/`);
  return true;
}

// -------------------------------------------------------------
// 📚 MatiereClasse (configuration matières par classe)
// -------------------------------------------------------------
export async function listMatieresClasses() {
  try {
    const { data } = await axiosClient.get('/matieres-classes/');
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('❌ listMatieresClasses error', e);
    return [];
  }
}

// -------------------------------------------------------------
// ⏰ Indisponibilités Professeurs
// -------------------------------------------------------------
export async function listIndisposProfesseurs(professeurId = null) {
  try {
    const url = professeurId ? `/indispos-profs/?professeur=${professeurId}` : '/indispos-profs/';
    const { data } = await axiosClient.get(url);
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('❌ listIndisposProfesseurs error', e);
    return [];
  }
}
export async function createIndispoProf(payload) {
  const { data } = await axiosClient.post('/indispos-profs/', payload);
  return data;
}
export async function updateIndispoProf(id, payload) {
  const { data } = await axiosClient.put(`/indispos-profs/${id}/`, payload);
  return data;
}
export async function deleteIndispoProf(id) {
  await axiosClient.delete(`/indispos-profs/${id}/`);
  return true;
}

// -------------------------------------------------------------
// 🎯 Contraintes Emploi du Temps
// -------------------------------------------------------------
export async function getContraintesEDT() {
  try {
    const { data } = await axiosClient.get('/contraintes-edt/');
    // list endpoint returns array [obj]; normalize single object
    if (Array.isArray(data)) return data[0] || null;
    return data;
  } catch (e) {
    console.error('❌ getContraintesEDT error', e);
    return null;
  }
}
export async function saveContraintesEDT(payload) {
  // Upsert semantics: POST without id will replace existing server-side
  const existing = await getContraintesEDT();
  if (existing && existing.id) {
    const { data } = await axiosClient.put(`/contraintes-edt/${existing.id}/`, payload);
    return data;
  }
  const { data } = await axiosClient.post('/contraintes-edt/', payload);
  return data;
}

// (fonctions emplois déjà définies plus haut)

// ======================================================
// 📝 INSCRIPTIONS / RÉINSCRIPTIONS EN LIGNE
// ======================================================

// --- Public (sans auth) ---
export async function getPublicEtablissements() {
  try {
    const { data } = await axiosClient.get("/public/etablissements/");
    return data;
  } catch (error) {
    console.error("❌ getPublicEtablissements:", error);
    return [];
  }
}

export async function getPublicVitrine(slug) {
  try {
    const { data } = await axiosClient.get(`/public/etablissement/${slug}/`);
    return data;
  } catch (error) {
    console.error("❌ getPublicVitrine:", error);
    return null;
  }
}

export async function publicInscriptionSubmit(payload) {
  const { data } = await axiosClient.post("/public/inscription/", payload);
  return data;
}

export async function getPublicInscriptionStatus(token) {
  try {
    const { data } = await axiosClient.get(`/public/inscription/${token}/status/`);
    return data;
  } catch (error) {
    console.error("❌ getPublicInscriptionStatus:", error);
    return null;
  }
}

export async function publicInscriptionUploadDocument(token, payload) {
  const form = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined && v !== null) form.append(k, v);
  });
  const { data } = await axiosClient.post(
    `/public/inscription/${token}/document/`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

// --- Parent connecté ---
export async function getParentInscriptions() {
  try {
    const { data } = await axiosClient.get("/parent/inscriptions/");
    return data;
  } catch (error) {
    console.error("❌ getParentInscriptions:", error);
    return [];
  }
}

export async function parentInscriptionSubmit(payload) {
  const { data } = await axiosClient.post("/parent/inscription/", payload);
  return data;
}

export async function parentInscriptionUploadDocument(dossierId, payload) {
  const form = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined && v !== null) form.append(k, v);
  });
  const { data } = await axiosClient.post(
    `/parent/inscription/${dossierId}/document/`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

// --- Admin inscriptions ---
export async function getAdminInscriptions(params = {}) {
  try {
    const { data } = await axiosClient.get("/inscriptions/", { params });
    return data;
  } catch (error) {
    console.error("❌ getAdminInscriptions:", error);
    return [];
  }
}

export async function getAdminInscriptionDetail(id) {
  try {
    const { data } = await axiosClient.get(`/inscriptions/${id}/`);
    return data;
  } catch (error) {
    console.error("❌ getAdminInscriptionDetail:", error);
    return null;
  }
}

export async function adminInscriptionChangerStatut(id, payload) {
  const { data } = await axiosClient.post(`/inscriptions/${id}/changer-statut/`, payload);
  return data;
}

export async function adminInscriptionValiderDocument(dossierId, payload) {
  const { data } = await axiosClient.post(`/inscriptions/${dossierId}/valider-document/`, payload);
  return data;
}

export async function getAdminInscriptionStats() {
  try {
    const { data } = await axiosClient.get("/inscriptions/stats/");
    return data;
  } catch (error) {
    console.error("❌ getAdminInscriptionStats:", error);
    return { total: 0, par_statut: {} };
  }
}
// 📚 ESPACE PÉDAGOGIQUE — Chapitres & Contenus de cours
// ======================================================

export async function getMesMatieresProf() {
  const { data } = await axiosClient.get('/chapitres/mes-matieres/');
  return data;
}

export async function getMesClassesProf() {
  const { data } = await axiosClient.get('/chapitres/mes-classes/');
  return data;
}

export async function getChapitres(params = {}) {
  const { data } = await axiosClient.get('/chapitres/', { params });
  return data;
}

export async function createChapitre(payload) {
  const { data } = await axiosClient.post('/chapitres/', payload);
  return data;
}

export async function updateChapitre(id, payload) {
  const { data } = await axiosClient.patch(`/chapitres/${id}/`, payload);
  return data;
}

export async function deleteChapitre(id) {
  await axiosClient.delete(`/chapitres/${id}/`);
}

export async function createCoursContenu(formData) {
  const { data } = await axiosClient.post('/cours-contenus/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function updateCoursContenu(id, formData) {
  const { data } = await axiosClient.patch(`/cours-contenus/${id}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function deleteCoursContenu(id) {
  await axiosClient.delete(`/cours-contenus/${id}/`);
}

// ============================================================
// ============  SIGNATURE ÉLECTRONIQUE  =======================
// ============================================================

export async function listDocumentsASignature(params = {}) {
  const { data } = await axiosClient.get('/signatures/documents/', { params });
  return data;
}

export async function getDocumentASignature(id) {
  const { data } = await axiosClient.get(`/signatures/documents/${id}/`);
  return data;
}

export async function createDocumentASignature(payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.post('/signatures/documents/', payload, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
  });
  return data;
}

export async function updateDocumentASignature(id, payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.patch(`/signatures/documents/${id}/`, payload, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
  });
  return data;
}

export async function deleteDocumentASignature(id) {
  await axiosClient.delete(`/signatures/documents/${id}/`);
}

// ============================================================
// 💰 COMPTABILITÉ ÉTABLISSEMENT
// ============================================================

// --- Catégories budget ---
export async function listComptaCategories(params = {}) {
  const { data } = await axiosClient.get('/compta/categories/', { params });
  return Array.isArray(data) ? data : data.results || [];
}
export async function createComptaCategorie(payload) {
  const { data } = await axiosClient.post('/compta/categories/', payload);
  return data;
}
export async function updateComptaCategorie(id, payload) {
  const { data } = await axiosClient.patch(`/compta/categories/${id}/`, payload);
  return data;
}
export async function deleteComptaCategorie(id) {
  await axiosClient.delete(`/compta/categories/${id}/`);
}

// --- Budget prévisionnel ---
export async function listComptaBudgets(params = {}) {
  const { data } = await axiosClient.get('/compta/budgets/', { params });
  return Array.isArray(data) ? data : data.results || [];
}
export async function createComptaBudget(payload) {
  const { data } = await axiosClient.post('/compta/budgets/', payload);
  return data;
}
export async function updateComptaBudget(id, payload) {
  const { data } = await axiosClient.patch(`/compta/budgets/${id}/`, payload);
  return data;
}
export async function deleteComptaBudget(id) {
  await axiosClient.delete(`/compta/budgets/${id}/`);
}
export async function getBudgetSynthese(anneeScolaire) {
  try {
    const { data } = await axiosClient.get('/compta/budgets/synthese/', { params: { annee_scolaire: anneeScolaire } });
    return data;
  } catch (error) {
    console.error("❌ getBudgetSynthese:", error);
    return { lignes: [], totaux: {} };
  }
}

// --- Transactions (dépenses / recettes) ---
export async function listComptaTransactions(params = {}) {
  const { data } = await axiosClient.get('/compta/transactions/', { params });
  return Array.isArray(data) ? data : data.results || [];
}
export async function createComptaTransaction(payload) {
  const { data } = await axiosClient.post('/compta/transactions/', payload);
  return data;
}
export async function updateComptaTransaction(id, payload) {
  const { data } = await axiosClient.patch(`/compta/transactions/${id}/`, payload);
  return data;
}
export async function deleteComptaTransaction(id) {
  await axiosClient.delete(`/compta/transactions/${id}/`);
}
export async function getComptaStats(params = {}) {
  try {
    const { data } = await axiosClient.get('/compta/transactions/stats/', { params });
    return data;
  } catch (error) {
    console.error("❌ getComptaStats:", error);
    return { total_depenses: "0", total_recettes: "0", solde: "0", non_rapprochees: 0 };
  }
}
export async function pointerTransaction(id) {
  const { data } = await axiosClient.post(`/compta/transactions/${id}/pointer/`);
  return data;
}

// --- Rapprochement bancaire ---
export async function listRapprochements(params = {}) {
  const { data } = await axiosClient.get('/compta/rapprochements/', { params });
  return Array.isArray(data) ? data : data.results || [];
}
export async function createRapprochement(payload) {
  const { data } = await axiosClient.post('/compta/rapprochements/', payload);
  return data;
}
export async function validerRapprochement(id) {
  try {
    const { data } = await axiosClient.post(`/compta/rapprochements/${id}/valider/`);
    return data;
  } catch (error) {
    return error.response?.data || { detail: "Erreur lors de la validation" };
  }
}
export async function getRapprochementTransactions(id) {
  const { data } = await axiosClient.get(`/compta/rapprochements/${id}/transactions/`);
  return Array.isArray(data) ? data : data.results || [];
}

// --- Export comptable ---
export async function exportComptaCSV(dateDebut, dateFin) {
  const response = await axiosClient.get('/compta/export/csv/', {
    params: { date_debut: dateDebut, date_fin: dateFin },
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `export_compta_${dateDebut}_${dateFin}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function signerDocument(documentId, signatureImage) {
  const { data } = await axiosClient.post(
    `/signatures/documents/${documentId}/signer/`,
    { signature_image: signatureImage }
  );
  return data;
}

export async function getMaSignature(documentId) {
  const { data } = await axiosClient.get(`/signatures/documents/${documentId}/ma-signature/`);
  return data;
}

export async function getSignaturesDocument(documentId) {
  const { data } = await axiosClient.get(`/signatures/documents/${documentId}/signatures/`);
  return data;
}

export async function getMesSignatures() {
  const { data } = await axiosClient.get('/signatures/documents/mes-signatures/');
  return data;
}

export async function getDocumentsEnAttente() {
  const { data } = await axiosClient.get('/signatures/documents/en-attente/');
  return data;
}

export async function getStatsSignatures() {
  const { data } = await axiosClient.get('/signatures/documents/stats/');
  return data;
}

// ===================== GED (Gestion Électronique des Documents) =====================

export async function listDocumentsGED(params = {}) {
  const { data } = await axiosClient.get('/ged/documents/', { params });
  return data;
}

export async function getDocumentGED(id) {
  const { data } = await axiosClient.get(`/ged/documents/${id}/`);
  return data;
}

export async function createDocumentGED(formData) {
  const { data } = await axiosClient.post('/ged/documents/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function updateDocumentGED(id, payload) {
  const { data } = await axiosClient.patch(`/ged/documents/${id}/`, payload);
  return data;
}

export async function deleteDocumentGED(id) {
  await axiosClient.delete(`/ged/documents/${id}/`);
}

export async function uploadNouvelleVersionGED(id, formData) {
  const { data } = await axiosClient.post(
    `/ged/documents/${id}/nouvelle-version/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function getHistoriqueGED(id) {
  const { data } = await axiosClient.get(`/ged/documents/${id}/historique/`);
  return data;
}

export async function getStatsGED() {
  const { data } = await axiosClient.get('/ged/documents/stats/');
  return data;
}

export async function getAnneesGED() {
  const { data } = await axiosClient.get('/ged/documents/annees/');
  return data;
}

// ────────────────────────────────────────────────────────────
// 📒 LIVRETS SCOLAIRES (LSU / LSL)
// ────────────────────────────────────────────────────────────

/** Liste des livrets (filtrable par query params) */
export async function getLivrets(params = {}) {
  const { data } = await axiosClient.get('/livrets/', { params });
  return Array.isArray(data) ? data : data.results || [];
}

/** Mes livrets (élève/parent/prof) */
export async function getMesLivrets() {
  const { data } = await axiosClient.get('/livrets/mine/');
  return Array.isArray(data) ? data : data.results || [];
}

/** Détail d'un livret */
export async function getLivret(id) {
  const { data } = await axiosClient.get(`/livrets/${id}/`);
  return data;
}

/** Générer les livrets automatiquement pour une classe/période */
export async function genererLivrets(payload) {
  const { data } = await axiosClient.post('/livrets/generer/', payload);
  return data;
}

/** Sauvegarder les appréciations d'un livret (prof) */
export async function sauvegarderAppreciations(livretId, payload) {
  const { data } = await axiosClient.patch(`/livrets/${livretId}/appreciations/`, payload);
  return data;
}

/** Valider un livret (prof) */
export async function validerLivret(livretId) {
  const { data } = await axiosClient.post(`/livrets/${livretId}/valider/`);
  return data;
}

/** Viser un livret (directeur) */
export async function viserLivret(livretId) {
  const { data } = await axiosClient.post(`/livrets/${livretId}/viser/`);
  return data;
}

/** Publier un livret (directeur) */
export async function publierLivret(livretId) {
  const { data } = await axiosClient.post(`/livrets/${livretId}/publier/`);
  return data;
}

/** Publication en masse */
export async function publierLivretsMasse(payload) {
  const { data } = await axiosClient.post('/livrets/publier-masse/', payload);
  return data;
}

/** URL d'export PDF d'un livret */
export function getLivretPdfUrl(livretId) {
  return `${axiosClient.defaults.baseURL}/livrets/${livretId}/export-pdf/`;
}

/** URL d'export XML officiel d'un livret */
export function getLivretXmlUrl(livretId) {
  return `${axiosClient.defaults.baseURL}/livrets/${livretId}/export-xml/`;
}

// =====================================================
// =============  GESTION RH  ==========================
// =====================================================

// --- Contrats ---
export async function listContratsPersonnel(params = {}) {
  const { data } = await axiosClient.get('/rh/contrats/', { params });
  return Array.isArray(data) ? data : data?.results || [];
}
export async function getContratPersonnel(id) {
  const { data } = await axiosClient.get(`/rh/contrats/${id}/`);
  return data;
}
export async function createContratPersonnel(payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.post('/rh/contrats/', payload, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
  });
  return data;
}
export async function updateContratPersonnel(id, payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.patch(`/rh/contrats/${id}/`, payload, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
  });
  return data;
}
export async function deleteContratPersonnel(id) {
  await axiosClient.delete(`/rh/contrats/${id}/`);
}
export async function getContratsExpiring() {
  const { data } = await axiosClient.get('/rh/contrats/expiring/');
  return Array.isArray(data) ? data : data?.results || [];
}
export async function getContratsStats() {
  const { data } = await axiosClient.get('/rh/contrats/stats/');
  return data;
}

// --- Absences personnel ---
export async function listAbsencesPersonnel(params = {}) {
  const { data } = await axiosClient.get('/rh/absences/', { params });
  return Array.isArray(data) ? data : data?.results || [];
}
export async function getAbsencePersonnel(id) {
  const { data } = await axiosClient.get(`/rh/absences/${id}/`);
  return data;
}
export async function createAbsencePersonnel(payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.post('/rh/absences/', payload, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
  });
  return data;
}
export async function updateAbsencePersonnel(id, payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.patch(`/rh/absences/${id}/`, payload, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
  });
  return data;
}
export async function deleteAbsencePersonnel(id) {
  await axiosClient.delete(`/rh/absences/${id}/`);
}
export async function approuverAbsencePersonnel(id, commentaire = '') {
  const { data } = await axiosClient.post(`/rh/absences/${id}/approuver/`, { commentaire });
  return data;
}
export async function refuserAbsencePersonnel(id, commentaire = '') {
  const { data } = await axiosClient.post(`/rh/absences/${id}/refuser/`, { commentaire });
  return data;
}
export async function getMesAbsencesPersonnel() {
  const { data } = await axiosClient.get('/rh/absences/mine/');
  return Array.isArray(data) ? data : data?.results || [];
}
export async function getAbsencesPersonnelStats() {
  const { data } = await axiosClient.get('/rh/absences/stats/');
  return data;
}

// --- Planning personnel ---
export async function listPlanningPersonnel(params = {}) {
  const { data } = await axiosClient.get('/rh/planning/', { params });
  return Array.isArray(data) ? data : data?.results || [];
}
export async function createPlanningPersonnel(payload) {
  const { data } = await axiosClient.post('/rh/planning/', payload);
  return data;
}
export async function updatePlanningPersonnel(id, payload) {
  const { data } = await axiosClient.patch(`/rh/planning/${id}/`, payload);
  return data;
}
export async function deletePlanningPersonnel(id) {
  await axiosClient.delete(`/rh/planning/${id}/`);
}

// ============================================================
// 🏢 GESTION DES STAGES
// ============================================================

// --- Stages (conventions) ---
export async function listStages(params = {}) {
  const { data } = await axiosClient.get('/stages/', { params });
  return Array.isArray(data) ? data : data?.results || [];
}

export async function getStage(id) {
  const { data } = await axiosClient.get(`/stages/${id}/`);
  return data;
}

export async function createStage(payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.post('/stages/', payload, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
  });
  return data;
}

export async function updateStage(id, payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.patch(`/stages/${id}/`, payload, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
  });
  return data;
}

export async function deleteStage(id) {
  await axiosClient.delete(`/stages/${id}/`);
}

export async function validerStage(id) {
  const { data } = await axiosClient.post(`/stages/${id}/valider/`);
  return data;
}

export async function demarrerStage(id) {
  const { data } = await axiosClient.post(`/stages/${id}/demarrer/`);
  return data;
}

export async function terminerStage(id) {
  const { data } = await axiosClient.post(`/stages/${id}/terminer/`);
  return data;
}

export async function getStagesStats() {
  const { data } = await axiosClient.get('/stages/stats/');
  return data;
}

// --- Suivis de stage ---
export async function listSuivisStage(params = {}) {
  const { data } = await axiosClient.get('/stages-suivis/', { params });
  return Array.isArray(data) ? data : data?.results || [];
}

export async function createSuiviStage(payload) {
  const { data } = await axiosClient.post('/stages-suivis/', payload);
  return data;
}

export async function updateSuiviStage(id, payload) {
  const { data } = await axiosClient.patch(`/stages-suivis/${id}/`, payload);
  return data;
}

export async function deleteSuiviStage(id) {
  await axiosClient.delete(`/stages-suivis/${id}/`);
}

// --- Évaluations de stage ---
export async function listEvaluationsStage(params = {}) {
  const { data } = await axiosClient.get('/stages-evaluations/', { params });
  return Array.isArray(data) ? data : data?.results || [];
}

export async function createEvaluationStage(payload) {
  const { data } = await axiosClient.post('/stages-evaluations/', payload);
  return data;
}

export async function updateEvaluationStage(id, payload) {
  const { data } = await axiosClient.patch(`/stages-evaluations/${id}/`, payload);
  return data;
}

export async function deleteEvaluationStage(id) {
  await axiosClient.delete(`/stages-evaluations/${id}/`);
}

// --- Rapports de stage ---
export async function listRapportsStage(params = {}) {
  const { data } = await axiosClient.get('/stages-rapports/', { params });
  return Array.isArray(data) ? data : data?.results || [];
}

export async function getRapportStage(id) {
  const { data } = await axiosClient.get(`/stages-rapports/${id}/`);
  return data;
}

//
// 📊 PILOTAGE DIRECTEUR — KPIs établissement
//
// GET /api/directeur/kpis/ (directeur/admin/superadmin, scope établissement).
// Renvoie effectifs / présence / pédagogie / finance / alertes.
// On laisse l'erreur remonter pour que la page affiche un état "erreur + réessayer".
export async function getDirecteurKpis() {
  const { data } = await axiosClient.get("/directeur/kpis/");
  return data;
}

export async function createRapportStage(payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.post('/stages-rapports/', payload, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
  });
  return data;
}

export async function updateRapportStage(id, payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.patch(`/stages-rapports/${id}/`, payload, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
  });
  return data;
}

export async function validerRapportStage(id, payload = {}) {
  const { data } = await axiosClient.post(`/stages-rapports/${id}/valider/`, payload);
  return data;
}

export async function refuserRapportStage(id, payload = {}) {
  const { data } = await axiosClient.post(`/stages-rapports/${id}/refuser/`, payload);
  return data;
}


// ──────────────────────────────────────────────
// 📝 EXAMENS BLANCS
// ──────────────────────────────────────────────

export async function getExamensBlancs(params = {}) {
  const { data } = await axiosClient.get("/examens-blancs/", { params });
  return Array.isArray(data) ? data : data.results || [];
}

export async function getExamenBlancDetail(id) {
  const { data } = await axiosClient.get(`/examens-blancs/${id}/`);
  return data;
}

export async function getExamensBlancsmine() {
  const { data } = await axiosClient.get("/examens-blancs/mine/");
  return Array.isArray(data) ? data : [];
}

export async function createExamenBlanc(payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.post("/examens-blancs/", payload, {
    headers: isFormData ? { "Content-Type": "multipart/form-data" } : {},
  });
  return data;
}

export async function updateExamenBlanc(id, payload) {
  const isFormData = payload instanceof FormData;
  const { data } = await axiosClient.patch(`/examens-blancs/${id}/`, payload, {
    headers: isFormData ? { "Content-Type": "multipart/form-data" } : {},
  });
  return data;
}

export async function deleteExamenBlanc(id) {
  await axiosClient.delete(`/examens-blancs/${id}/`);
}

export async function initCopiesExamen(id) {
  const { data } = await axiosClient.post(`/examens-blancs/${id}/init-copies/`);
  return data;
}

export async function saisieNotesExamen(id, notes) {
  const { data } = await axiosClient.post(`/examens-blancs/${id}/saisie-notes/`, { notes });
  return data;
}

export async function publierExamenBlanc(id) {
  const { data } = await axiosClient.post(`/examens-blancs/${id}/publier/`);
  return data;
}

export async function getStatsExamen(id) {
  const { data } = await axiosClient.get(`/examens-blancs/${id}/statistiques/`);
  return data;
}

export async function getStatsGlobalesExamens(params = {}) {
  const { data } = await axiosClient.get("/examens-blancs/stats-globales/", { params });
  return data;
}

export async function checkExamAvailability(params = {}) {
  const { data } = await axiosClient.get("/examens-blancs/check-availability/", { params });
  return data;
}

export async function getCopiesExamen(examenId) {
  const { data } = await axiosClient.get("/examens-blancs-copies/", { params: { examen: examenId } });
  return Array.isArray(data) ? data : data.results || [];
}

export async function getMesCopiesExamensBlancs() {
  const { data } = await axiosClient.get("/examens-blancs-copies/");
  return Array.isArray(data) ? data : data.results || [];
}

//
// 🎥 CLASSES VIRTUELLES
//
export async function getClassesVirtuelles(params = {}) {
  try {
    const { data } = await axiosClient.get("/classes-virtuelles/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error("❌ Erreur classes virtuelles :", error);
    return [];
  }
}

export async function getClasseVirtuelle(id) {
  const { data } = await axiosClient.get(`/classes-virtuelles/${id}/`);
  return data;
}

export async function createClasseVirtuelle(payload) {
  const { data } = await axiosClient.post("/classes-virtuelles/", payload);
  return data;
}

export async function updateClasseVirtuelle(id, payload) {
  const { data } = await axiosClient.patch(`/classes-virtuelles/${id}/`, payload);
  return data;
}

export async function deleteClasseVirtuelle(id) {
  await axiosClient.delete(`/classes-virtuelles/${id}/`);
}

export async function demarrerClasseVirtuelle(id) {
  const { data } = await axiosClient.post(`/classes-virtuelles/${id}/demarrer/`);
  return data;
}

export async function terminerClasseVirtuelle(id) {
  const { data } = await axiosClient.post(`/classes-virtuelles/${id}/terminer/`);
  return data;
}

export async function rejoindreClasseVirtuelle(id) {
  const { data } = await axiosClient.post(`/classes-virtuelles/${id}/rejoindre/`);
  return data;
}

export async function quitterClasseVirtuelle(id) {
  const { data } = await axiosClient.post(`/classes-virtuelles/${id}/quitter/`);
  return data;
}

export async function getParticipantsClasseVirtuelle(id) {
  const { data } = await axiosClient.get(`/classes-virtuelles/${id}/participants/`);
  return Array.isArray(data) ? data : data.results || [];
}

export async function getEnregistrementsClasseVirtuelle(sessionId) {
  const { data } = await axiosClient.get("/classes-virtuelles-enregistrements/", { params: { session: sessionId } });
  return Array.isArray(data) ? data : data.results || [];
}

export async function createEnregistrementClasseVirtuelle(payload) {
  const { data } = await axiosClient.post("/classes-virtuelles-enregistrements/", payload);
  return data;
}

export async function deleteEnregistrementClasseVirtuelle(id) {
  await axiosClient.delete(`/classes-virtuelles-enregistrements/${id}/`);
}

//
// 📅 RENDEZ-VOUS (Créneaux + Réservations)
//
export async function listCreneauxRdv(params = {}) {
  try {
    const { data } = await axiosClient.get("/creneaux-rdv/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (e) {
    console.error("❌ listCreneauxRdv error", e);
    return [];
  }
}
export async function createCreneauRdv(payload) {
  const { data } = await axiosClient.post("/creneaux-rdv/", payload);
  return data;
}
export async function updateCreneauRdv(id, payload) {
  const { data } = await axiosClient.patch(`/creneaux-rdv/${id}/`, payload);
  return data;
}
export async function deleteCreneauRdv(id) {
  await axiosClient.delete(`/creneaux-rdv/${id}/`);
}
export async function listRendezVous(params = {}) {
  try {
    const { data } = await axiosClient.get("/rendez-vous/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (e) {
    console.error("❌ listRendezVous error", e);
    return [];
  }
}
export async function createRendezVous(payload) {
  const { data } = await axiosClient.post("/rendez-vous/", payload);
  return data;
}
export async function confirmerRendezVous(id) {
  const { data } = await axiosClient.post(`/rendez-vous/${id}/confirmer/`);
  return data;
}
export async function annulerRendezVous(id, commentaire = "") {
  const { data } = await axiosClient.post(`/rendez-vous/${id}/annuler/`, { commentaire });
  return data;
}
export async function getDisponibilitesRdv(params) {
  const { data } = await axiosClient.get("/creneaux-rdv/disponibilites/", { params });
  return data;
}

//
// 🚌 SORTIES SCOLAIRES (Bloc D)
//
// Liste des sorties (scope automatique côté backend selon le rôle :
//   staff voit l'établissement, élève/parent voient les leurs).
export async function getSorties(params = {}) {
  try {
    const { data } = await axiosClient.get("/sorties/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des sorties :", error);
    return [];
  }
}
// Création d'une sortie (staff : administratif/directeur).
export async function createSortie(payload) {
  const { data } = await axiosClient.post("/sorties/", payload);
  return data;
}
// Mise à jour d'une sortie (staff).
export async function updateSortie(id, payload) {
  const { data } = await axiosClient.patch(`/sorties/${id}/`, payload);
  return data;
}
// Suppression d'une sortie (staff).
export async function deleteSortie(id) {
  await axiosClient.delete(`/sorties/${id}/`);
  return true;
}

// Liste des autorisations de sortie (parent voit celles de ses enfants,
//   élève les siennes, staff celles de l'établissement).
export async function getSortiesAutorisations(params = {}) {
  try {
    const { data } = await axiosClient.get("/sorties-autorisations/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des autorisations de sortie :", error);
    return [];
  }
}
// Réponse d'un parent à une autorisation : statut "autorise" | "refuse" + commentaire optionnel.
//   Le backend pose parent + date_reponse automatiquement.
export async function repondreAutorisationSortie(id, { statut, commentaire = "" }) {
  const { data } = await axiosClient.patch(`/sorties-autorisations/${id}/`, { statut, commentaire });
  return data;
}

//
// 📨 CONVOCATIONS (Bloc D)
//
// Liste des convocations (scope automatique côté backend selon le rôle :
//   staff voit l'établissement, élève les siennes, parent celles de ses enfants).
export async function getConvocations(params = {}) {
  try {
    const { data } = await axiosClient.get("/convocations/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des convocations :", error);
    return [];
  }
}
// Création d'une convocation (staff : administratif/directeur).
export async function createConvocation(payload) {
  const { data } = await axiosClient.post("/convocations/", payload);
  return data;
}
// Mise à jour d'une convocation (staff).
export async function updateConvocation(id, payload) {
  const { data } = await axiosClient.patch(`/convocations/${id}/`, payload);
  return data;
}
// Suppression d'une convocation (staff).
export async function deleteConvocation(id) {
  await axiosClient.delete(`/convocations/${id}/`);
  return true;
}
// Accusé de réception (parent de l'élève ou élève destinataire) : passe le statut à "accusee".
export async function accuserConvocation(id) {
  const { data } = await axiosClient.post(`/convocations/${id}/accuser/`);
  return data;
}

//
// 📄 CONVENTIONS DE STAGE (Bloc D)
//
// Liste des conventions de stage (scope automatique côté backend selon le rôle :
//   staff voit l'établissement, élève les siennes, parent celles de ses enfants).
//   Filtres optionnels : { eleve, statut }.
export async function getConventionsStage(params = {}) {
  try {
    const { data } = await axiosClient.get("/conventions-stage/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des conventions de stage :", error);
    return [];
  }
}
// Création d'une convention de stage (staff : administratif/directeur).
export async function createConventionStage(payload) {
  const { data } = await axiosClient.post("/conventions-stage/", payload);
  return data;
}
// Mise à jour d'une convention de stage (staff).
export async function updateConventionStage(id, payload) {
  const { data } = await axiosClient.patch(`/conventions-stage/${id}/`, payload);
  return data;
}
// Suppression d'une convention de stage (staff).
export async function deleteConventionStage(id) {
  await axiosClient.delete(`/conventions-stage/${id}/`);
  return true;
}

//
// 🚌 TRANSPORT SCOLAIRE (Bloc D)
//
// --- Lignes ---
// Liste des lignes de transport (scope automatique côté backend selon le rôle :
//   staff voit l'établissement, élève/parent en lecture seule).
export async function getTransportLignes(params = {}) {
  try {
    const { data } = await axiosClient.get("/transport/lignes/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des lignes de transport :", error);
    return [];
  }
}
// Création d'une ligne (staff : administratif/directeur).
export async function createTransportLigne(payload) {
  const { data } = await axiosClient.post("/transport/lignes/", payload);
  return data;
}
// Mise à jour d'une ligne (staff).
export async function updateTransportLigne(id, payload) {
  const { data } = await axiosClient.patch(`/transport/lignes/${id}/`, payload);
  return data;
}
// Suppression d'une ligne (staff).
export async function deleteTransportLigne(id) {
  await axiosClient.delete(`/transport/lignes/${id}/`);
  return true;
}

// --- Arrêts ---
// Liste des arrêts (filtrer par ligne via params { ligne: <id> }).
export async function getTransportArrets(params = {}) {
  try {
    const { data } = await axiosClient.get("/transport/arrets/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des arrêts de transport :", error);
    return [];
  }
}
// Création d'un arrêt (staff).
export async function createTransportArret(payload) {
  const { data } = await axiosClient.post("/transport/arrets/", payload);
  return data;
}
// Mise à jour d'un arrêt (staff).
export async function updateTransportArret(id, payload) {
  const { data } = await axiosClient.patch(`/transport/arrets/${id}/`, payload);
  return data;
}
// Suppression d'un arrêt (staff).
export async function deleteTransportArret(id) {
  await axiosClient.delete(`/transport/arrets/${id}/`);
  return true;
}

// --- Abonnements ---
// Liste des abonnements (filtres possibles : { eleve, ligne, statut }).
//   Scope automatique côté backend : parent voit ceux de ses enfants,
//   élève le sien, staff ceux de l'établissement.
export async function getTransportAbonnements(params = {}) {
  try {
    const { data } = await axiosClient.get("/transport/abonnements/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des abonnements de transport :", error);
    return [];
  }
}
// Création d'un abonnement (staff). Unicité (eleve, ligne) -> 400 si doublon.
export async function createTransportAbonnement(payload) {
  const { data } = await axiosClient.post("/transport/abonnements/", payload);
  return data;
}
// Mise à jour d'un abonnement (staff).
export async function updateTransportAbonnement(id, payload) {
  const { data } = await axiosClient.patch(`/transport/abonnements/${id}/`, payload);
  return data;
}
// Suppression d'un abonnement (staff).
export async function deleteTransportAbonnement(id) {
  await axiosClient.delete(`/transport/abonnements/${id}/`);
  return true;
}

//
// 🎨 ACTIVITÉS PÉRISCOLAIRES (Bloc D)
//
// --- Activités ---
// Liste des activités périscolaires (filtres possibles : { active, jour_semaine }).
//   Scope automatique côté backend : staff CRUD ; élève/parent en lecture seule
//   (activités de leur établissement).
export async function getActivitesPeriscolaires(params = {}) {
  try {
    const { data } = await axiosClient.get("/periscolaire/activites/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des activités périscolaires :", error);
    return [];
  }
}
// Création d'une activité (staff : administratif/directeur).
export async function createActivitePeriscolaire(payload) {
  const { data } = await axiosClient.post("/periscolaire/activites/", payload);
  return data;
}
// Mise à jour d'une activité (staff).
export async function updateActivitePeriscolaire(id, payload) {
  const { data } = await axiosClient.patch(`/periscolaire/activites/${id}/`, payload);
  return data;
}
// Suppression d'une activité (staff).
export async function deleteActivitePeriscolaire(id) {
  await axiosClient.delete(`/periscolaire/activites/${id}/`);
  return true;
}

// --- Inscriptions ---
// Liste des inscriptions (filtres possibles : { activite, eleve, statut }).
//   Scope automatique côté backend : staff voit l'établissement, parent celles
//   de ses enfants, élève les siennes.
export async function getInscriptionsPeriscolaires(params = {}) {
  try {
    const { data } = await axiosClient.get("/periscolaire/inscriptions/", { params });
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error("❌ Erreur lors du chargement des inscriptions périscolaires :", error);
    return [];
  }
}
// Création d'une inscription. Staff inscrit n'importe quel élève de l'établissement ;
//   parent inscrit uniquement ses enfants. Unicité (activite, eleve) -> 400 si doublon.
export async function createInscriptionPeriscolaire(payload) {
  const { data } = await axiosClient.post("/periscolaire/inscriptions/", payload);
  return data;
}
// Mise à jour d'une inscription (ex. statut -> annule).
export async function updateInscriptionPeriscolaire(id, payload) {
  const { data } = await axiosClient.patch(`/periscolaire/inscriptions/${id}/`, payload);
  return data;
}
// Suppression d'une inscription.
export async function deleteInscriptionPeriscolaire(id) {
  await axiosClient.delete(`/periscolaire/inscriptions/${id}/`);
  return true;
}

//
// 🎒 MA CLASSE (annuaire élève — Bloc D)
//
// Annuaire de la classe de l'élève connecté : sa classe, ses camarades et
//   ses profs (une ligne par matière). `classe` peut être null si l'élève
//   n'est rattaché à aucune classe. Forme garantie même en cas d'erreur.
export async function getMaClasse() {
  try {
    const { data } = await axiosClient.get("/eleve/ma-classe/");
    return {
      classe: data?.classe ?? null,
      camarades: Array.isArray(data?.camarades) ? data.camarades : [],
      profs: Array.isArray(data?.profs) ? data.profs : [],
    };
  } catch (error) {
    console.error("❌ Erreur lors du chargement de ma classe :", error);
    throw error;
  }
}

